import React, { useEffect, useState, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { useTournament } from "@/lib/TournamentContext";
import { toast } from "sonner";
import { Star, Zap, Lock, Info, AlertTriangle, Check, Loader2, RotateCw } from "lucide-react";
import TeamBadge from "@/components/app/TeamBadge";
import { computeUnderdog } from "@/lib/underdog";
import { UnderdogBadge, UnderdogTag, UnderdogLegend } from "@/components/app/UnderdogBadge";

const STAGE_LABELS = { group: "Group Stage", r32: "Round of 32", r16: "Round of 16", qf: "Quarter Finals", sf: "Semi Finals", final: "Final" };
const bucketKey = (m) => (m.matchday != null && m.stage === "group") ? `md:${m.matchday}` : `stage:${m.stage}`;
const bucketLabel = (m, isLeague) => (m.matchday != null && m.stage === "group") ? `${isLeague ? "Gameweek" : "Matchday"} ${m.matchday}` : (STAGE_LABELS[m.stage] || (m.stage || "").toUpperCase());

const ScorePicker = ({ value, onChange, onBlur, testid }) => (
  <input
    data-testid={testid}
    type="number" min={0} max={12}
    value={value == null ? "" : value}
    placeholder="–"
    onChange={(e) => {
      const raw = e.target.value;
      if (raw === "") return onChange(null);
      onChange(Math.min(12, Math.max(0, parseInt(raw, 10))));
    }}
    onBlur={onBlur}
    className="w-14 md:w-16 bg-zinc-950 border border-zinc-800 text-center font-display text-2xl py-1.5 focus:border-primary-accent focus:outline-none placeholder:text-zinc-600"
  />
);

const BreakdownChip = ({ label, value, accent }) => (
  <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 border ${accent ? "border-primary-accent/50 text-primary-accent" : "border-zinc-700 text-zinc-400"}`}>
    {label} +{value}
  </span>
);

function PredictionResult({ pred }) {
  const bd = pred.points_breakdown;
  return (
    <div className="md:text-right" data-testid={`pred-result-${pred.match_id}`}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">
        Your pick {pred.home_pred}-{pred.away_pred}
      </div>
      <div className={`font-display text-3xl ${pred.points > 0 ? "text-primary-accent" : "text-zinc-500"}`} data-testid={`pred-points-${pred.match_id}`}>
        +{pred.points} <span className="text-sm">pts</span>
      </div>
      {bd ? (
        <div className="flex flex-wrap gap-1 md:justify-end mt-1.5">
          {bd.result > 0 && <BreakdownChip label="Result" value={bd.result} />}
          {bd.gd > 0 && <BreakdownChip label="GD" value={bd.gd} />}
          {bd.exact > 0 && <BreakdownChip label="Exact" value={bd.exact} />}
          {bd.underdog > 0 && <BreakdownChip label="Underdog" value={bd.underdog} accent />}
          {bd.penalty_bonus > 0 && <BreakdownChip label="Pens" value={bd.penalty_bonus} accent />}
          {bd.multiplier > 1 && (
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-yellow-400/50 text-yellow-400">×{bd.multiplier}</span>
          )}
          {bd.base === 0 && <span className="text-[10px] uppercase tracking-wider text-zinc-600 py-0.5">Missed</span>}
        </div>
      ) : null}
    </div>
  );
}

// Per-row autosave status indicator (no buttons — saves happen on blur / toggle)
const SaveStatus = ({ status, hasPred, onRetry, testid }) => {
  if (status === "saving") {
    return (
      <div data-testid={testid} className="flex items-center justify-center gap-1.5 text-xs uppercase tracking-widest text-zinc-400 py-2">
        <Loader2 size={12} className="animate-spin" /> Saving…
      </div>
    );
  }
  if (status === "error") {
    return (
      <button data-testid={testid} onClick={onRetry}
        className="flex items-center justify-center gap-1.5 text-xs uppercase tracking-widest text-primary-accent hover:text-white py-2">
        <RotateCw size={12} /> Retry
      </button>
    );
  }
  if (status === "saved" || hasPred) {
    return (
      <div data-testid={testid} className="flex items-center justify-center gap-1.5 text-xs uppercase tracking-widest text-green-400 py-2">
        <Check size={12} /> Saved
      </div>
    );
  }
  return (
    <div data-testid={testid} className="text-center text-[10px] uppercase tracking-widest text-zinc-600 py-2">
      Auto-saves as you type
    </div>
  );
};

const MatchRow = ({ match, teams, pred, draft, onChangeDraft, onBlurSave, onRetry, status, finalised, usage, capMult, ppMult, isLeague, homeAdv = 75, koBonus = 3 }) => {
  const h = teams[match.home_team_id] || {};
  const a = teams[match.away_team_id] || {};
  const ud = computeUnderdog(h, a, homeAdv);
  const underdogName = ud ? (ud.underdogSide === "home" ? h.name : a.name) : null;
  const [feed, setFeed] = useState(null);

  const kickoff = new Date(match.kickoff);
  const locked = kickoff <= new Date();
  const editable = !locked && match.status !== "completed" && finalised;
  const scoresEntered = draft.home != null && draft.away != null;
  const isKO = match.stage && match.stage !== "group";
  const isDrawPred = scoresEntered && draft.home === draft.away;

  useEffect(() => {
    if (locked) {
      api.get(`/matches/${match.match_id}/prediction-feed`).then((r) => setFeed(r.data));
    }
  }, [locked, match.match_id]);

  return (
    <div className={`surface-card mb-3 transition-colors${locked ? " match-locked" : ""}`} data-testid={`match-row-${match.match_id}`}>
      <div className="p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-4">
        <div className="md:w-40 text-xs text-zinc-500 uppercase tracking-widest flex flex-col gap-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="inline-flex items-center px-1.5 py-0.5 border border-zinc-700 text-[10px] text-zinc-300 tracking-widest" data-testid={`md-tag-${match.match_id}`}>
              {(match.matchday != null && match.stage === "group") ? `${isLeague ? "GW" : "MD"}${match.matchday}` : (match.stage || "").toUpperCase()}
            </span>
            {match.group ? <span>Group {match.group}</span> : (!isLeague && <span>{match.stage.toUpperCase()}</span>)}
          </div>
          <span className="text-zinc-400">{kickoff.toLocaleString([], { dateStyle: "short", timeStyle: "short" })}</span>
          {locked && <span className="text-primary-accent flex items-center gap-1"><Lock size={10}/> Locked at kickoff</span>}
          {ud && <UnderdogBadge info={ud} underdogName={underdogName} className="mt-1" />}
        </div>

        <div className="flex-1 grid grid-cols-[1fr_auto_1fr] items-center gap-2">
          <div className="flex items-center gap-2 justify-end text-right min-w-0">
            {ud && ud.underdogSide === "home" && <UnderdogTag info={ud} />}
            <TeamBadge team={h} size="md" className="justify-end" />
            {h.elo != null ? <span className="text-[10px] text-zinc-600 font-display tabular-nums" data-testid={`team-elo-${match.home_team_id}`} title="Elo rating">{h.elo}</span>
              : h.fifa_rank ? <span className="text-[10px] text-zinc-600 font-display tabular-nums" data-testid={`team-rank-${match.home_team_id}`}>#{h.fifa_rank}</span> : null}
          </div>
          <div className="flex items-center justify-center gap-1 md:gap-2">
            {match.status === "completed" ? (
              <div className="font-display text-2xl md:text-3xl tabular-nums">
                {match.home_score} <span className="text-zinc-600">-</span> {match.away_score}
              </div>
            ) : editable ? (
              <>
                <ScorePicker testid={`pred-home-${match.match_id}`} value={draft.home} onChange={(v) => onChangeDraft(match.match_id, "home", v)} onBlur={() => onBlurSave(match.match_id)} />
                <span className="text-zinc-500">-</span>
                <ScorePicker testid={`pred-away-${match.match_id}`} value={draft.away} onChange={(v) => onChangeDraft(match.match_id, "away", v)} onBlur={() => onBlurSave(match.match_id)} />
              </>
            ) : !finalised ? (
              <span className="text-zinc-600 text-sm uppercase tracking-widest">TBD</span>
            ) : (match.status === "live" || match.home_score != null) ? (
              <div className="flex flex-col items-center" data-testid={`live-score-${match.match_id}`}>
                <div className="font-display text-2xl md:text-3xl tabular-nums text-green-400">
                  {match.home_score ?? 0} <span className="text-zinc-600">-</span> {match.away_score ?? 0}
                </div>
                <span className="text-[9px] uppercase tracking-widest text-green-400 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"/> Live
                </span>
              </div>
            ) : (
              <div className="font-display text-2xl tabular-nums text-zinc-300">
                {pred ? `${pred.home_pred} - ${pred.away_pred}` : "— : —"}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 justify-start min-w-0">
            {a.elo != null ? <span className="text-[10px] text-zinc-600 font-display tabular-nums" data-testid={`team-elo-${match.away_team_id}`} title="Elo rating">{a.elo}</span>
              : a.fifa_rank ? <span className="text-[10px] text-zinc-600 font-display tabular-nums" data-testid={`team-rank-${match.away_team_id}`}>#{a.fifa_rank}</span> : null}
            <TeamBadge team={a} size="md" />
            {ud && ud.underdogSide === "away" && <UnderdogTag info={ud} />}
          </div>
        </div>

        <div className="flex md:flex-col gap-2 md:w-44">
          {match.status === "completed" && pred?.settled ? (
            <PredictionResult pred={pred} />
          ) : !finalised ? (
            <div className="text-xs uppercase tracking-widest text-zinc-600 self-center" data-testid={`not-finalised-${match.match_id}`}>
              Teams not decided yet
            </div>
          ) : !locked ? (
            <>
              <div className="flex gap-2">
                <button
                  data-testid={`captain-${match.match_id}`}
                  onClick={() => onChangeDraft(match.match_id, "captain", !draft.captain, true)}
                  disabled={!scoresEntered}
                  title={scoresEntered ? `Captain: ${capMult}× points (once per matchday)` : "Enter a scoreline first"}
                  className={`flex-1 text-xs uppercase tracking-widest py-1.5 border disabled:opacity-40 disabled:cursor-not-allowed ${draft.captain ? "bg-yellow-400 text-black border-yellow-400" : "border-zinc-800 text-zinc-400 hover:border-yellow-400"}`}
                >
                  <Star size={10} className="inline mr-1"/>Captain
                </button>
                <button
                  data-testid={`powerplay-${match.match_id}`}
                  onClick={() => onChangeDraft(match.match_id, "pp", !draft.pp, true)}
                  disabled={!scoresEntered}
                  title={scoresEntered ? `Power Play: ${ppMult}× points (once per matchday)` : "Enter a scoreline first"}
                  className={`flex-1 text-xs uppercase tracking-widest py-1.5 border disabled:opacity-40 disabled:cursor-not-allowed ${draft.pp ? "bg-[var(--accent)] text-white border-[var(--accent)]" : "border-zinc-800 text-zinc-400 hover:border-[var(--accent)]"}`}
                >
                  <Zap size={10} className="inline mr-1"/>{ppMult}x
                </button>
              </div>
              <div className="flex gap-3 text-[9px] uppercase tracking-wider" data-testid={`chip-status-${match.match_id}`}>
                <span className={usage?.captain === match.match_id ? "text-yellow-400" : usage?.captain ? "text-zinc-600" : "text-zinc-500"}>
                  ★ {usage?.captain === match.match_id ? "Here" : usage?.captain ? "Used this MD" : "Free"}
                </span>
                <span className={usage?.pp === match.match_id ? "text-[var(--accent)]" : usage?.pp ? "text-zinc-600" : "text-zinc-500"}>
                  {ppMult}x {usage?.pp === match.match_id ? "Here" : usage?.pp ? "Used this MD" : "Free"}
                </span>
              </div>
              <SaveStatus testid={`save-status-${match.match_id}`} status={status} hasPred={!!pred} onRetry={() => onRetry(match.match_id)} />
            </>
          ) : pred ? (
            <div className="text-xs uppercase tracking-widest text-zinc-400">
              Your pick: <span className="text-white font-display text-lg">{pred.home_pred}-{pred.away_pred}</span>
              {isKO && pred.penalty_winner && (
                <div className="text-purple-300 mt-0.5 normal-case tracking-normal" data-testid={`pen-pick-${match.match_id}`}>
                  Pens: {(teams[pred.penalty_winner] || {}).code || (teams[pred.penalty_winner] || {}).name}
                </div>
              )}
              <div className="text-zinc-500 mt-1">{match.status === "completed" ? "Awaiting settlement" : "Awaiting result"}</div>
            </div>
          ) : (
            <div className="text-xs uppercase tracking-widest text-zinc-600">No prediction made</div>
          )}
        </div>
      </div>

      {/* Knockout penalty-winner picker — shown when you predict a DRAW after 120' */}
      {isKO && editable && isDrawPred && (
        <div className="border-t border-white/5 px-5 py-3 bg-purple-500/5" data-testid={`penalty-picker-${match.match_id}`}>
          <div className="text-[11px] uppercase tracking-widest text-purple-300 mb-2 flex items-center gap-1.5">
            <Zap size={12}/> Knockout draw — who wins on penalties? <span className="text-zinc-500 normal-case tracking-normal">(+{koBonus} bonus if correct)</span>
          </div>
          <div className="flex gap-2">
            {[{ id: match.home_team_id, t: h }, { id: match.away_team_id, t: a }].map(({ id, t }) => (
              <button
                key={id}
                data-testid={`penalty-pick-${match.match_id}-${id}`}
                onClick={() => onChangeDraft(match.match_id, "penaltyWinner", draft.penaltyWinner === id ? null : id, true)}
                className={`flex-1 flex items-center justify-center gap-1.5 text-xs uppercase tracking-widest py-2 border ${draft.penaltyWinner === id ? "bg-purple-500 text-white border-purple-500" : "border-zinc-800 text-zinc-300 hover:border-purple-400"}`}
              >
                {t.crest && <img src={t.crest} alt="" className="w-4 h-4 object-contain" />}
                {t.code || t.name}
              </button>
            ))}
          </div>
        </div>
      )}
      {locked && feed && feed.total > 0 && (
        <div className="border-t border-white/5 px-5 py-3 bg-black/40">
          <div className="label-tag mb-2">Crowd Picks ({feed.total})</div>
          <div className="flex gap-1 h-2 overflow-hidden mb-2">
            <div style={{ width: `${(feed.results.H/feed.total)*100}%`, background: "var(--primary)" }} title="Home wins"/>
            <div style={{ width: `${(feed.results.D/feed.total)*100}%`, background: "var(--muted)" }} title="Draws"/>
            <div style={{ width: `${(feed.results.A/feed.total)*100}%`, background: "var(--accent)" }} title="Away wins"/>
          </div>
          <div className="flex flex-wrap gap-3 text-xs text-zinc-400">
            <span>H {Math.round((feed.results.H/feed.total)*100)}%</span>
            <span>D {Math.round((feed.results.D/feed.total)*100)}%</span>
            <span>A {Math.round((feed.results.A/feed.total)*100)}%</span>
            <span className="text-zinc-500">Popular: {feed.top_scorelines.slice(0,3).map(s => `${s.score} (${s.count})`).join(", ")}</span>
          </div>
        </div>
      )}
    </div>
  );
};

const blankDraft = (p) => ({
  home: p ? p.home_pred : null,
  away: p ? p.away_pred : null,
  captain: !!p?.is_captain,
  pp: !!p?.power_play,
  penaltyWinner: p?.penalty_winner ?? null,
});

export default function Predict() {
  const { active } = useTournament();
  const capMult = active?.config?.captain_multiplier ?? 2;
  const ppMult = active?.config?.powerplay_multiplier ?? 3;
  const isLeague = active?.format === "league";
  const sjEnabled = isLeague && (active?.config?.super_jinx_enabled ?? true);
  const sjMult = active?.config?.super_jinx_multiplier ?? 1.5;
  const sjSplit = active?.config?.half_season_split ?? 19;
  const [superJinx, setSuperJinx] = useState({ used_gameweeks: [], half1_used: null, half2_used: null });
  const [sjCounts, setSjCounts] = useState({});
  const [sjConfirm, setSjConfirm] = useState(null); // { matchday, half, count }
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState({});
  const [preds, setPreds] = useState({});
  const [drafts, setDrafts] = useState({});
  const [saveStatus, setSaveStatus] = useState({});
  const [filter, setFilter] = useState("upcoming");
  const [showLegend, setShowLegend] = useState(false);
  const [foulDismissed, setFoulDismissed] = useState(false);
  const [confirmMove, setConfirmMove] = useState(null); // { mid, clashMid, fromLabel, toLabel, mdLabel }
  const touched = useRef(new Set());
  const draftsRef = useRef({});
  const predsRef = useRef({});

  useEffect(() => { draftsRef.current = drafts; }, [drafts]);
  useEffect(() => { predsRef.current = preds; }, [preds]);

  const matchById = useMemo(() => Object.fromEntries(matches.map((m) => [m.match_id, m])), [matches]);
  const matchLabel = (m) => `${teams[m?.home_team_id]?.code || "?"} v ${teams[m?.away_team_id]?.code || "?"}`;
  const isFinalised = (m) => {
    const hh = teams[m.home_team_id], aa = teams[m.away_team_id];
    return !!hh && hh.code !== "TBD" && !!aa && aa.code !== "TBD";
  };

  // Captain / Power Play usage per matchday bucket (from current drafts)
  const bucketUsage = useMemo(() => {
    const u = {};
    matches.forEach((m) => {
      const bk = bucketKey(m);
      if (!u[bk]) u[bk] = { captain: null, pp: null };
      const d = drafts[m.match_id];
      if (d?.captain) u[bk].captain = m.match_id;
      if (d?.pp) u[bk].pp = m.match_id;
    });
    return u;
  }, [matches, drafts]);

  // Existing rule violations in SAVED predictions (multiple captain/2x in one matchday)
  const fouls = useMemo(() => {
    const cap = {}, pp = {};
    Object.values(preds).forEach((p) => {
      const m = matchById[p.match_id];
      if (!m) return;
      const bk = bucketKey(m);
      if (p.is_captain) (cap[bk] = cap[bk] || []).push(m);
      if (p.power_play) (pp[bk] = pp[bk] || []).push(m);
    });
    const out = [];
    Object.values(cap).forEach((ms) => { if (ms.length > 1) out.push({ chip: "Captain", label: bucketLabel(ms[0]), matches: ms }); });
    Object.values(pp).forEach((ms) => { if (ms.length > 1) out.push({ chip: `Power Play (${ppMult}x)`, label: bucketLabel(ms[0]), matches: ms }); });
    return out;
  }, [preds, matchById, teams, ppMult]);

  useEffect(() => {
    if (!active) return;
    const tid = active.tournament_id;
    (async () => {
      const [mR, tR, pR] = await Promise.all([
        api.get(`/tournaments/${tid}/matches`),
        api.get(`/tournaments/${tid}/teams`),
        api.get(`/tournaments/${tid}/my-predictions`),
      ]);
      const predMap = Object.fromEntries(pR.data.map((p) => [p.match_id, p]));
      setMatches(mR.data);
      setTeams(Object.fromEntries(tR.data.map((t) => [t.team_id, t])));
      setPreds(predMap);
      setDrafts(Object.fromEntries(mR.data.map((m) => [m.match_id, blankDraft(predMap[m.match_id])])));
    })();
    // Keep live scores fresh on locked/in-play games (does not touch drafts).
    const poll = setInterval(() => {
      api.get(`/tournaments/${tid}/matches`).then((r) => setMatches(r.data)).catch(() => {});
    }, 30000);
    if (active.format === "league" && (active.config?.super_jinx_enabled ?? true)) {
      api.get(`/tournaments/${tid}/super-jinx/me`).then((r) => setSuperJinx(r.data)).catch(() => {});
      api.get(`/tournaments/${tid}/super-jinx/counts`).then((r) => setSjCounts(r.data || {})).catch(() => {});
    }
    return () => clearInterval(poll);
  }, [active]);

  const filtered = useMemo(() => {
    const liveMDs = active?.config?.live_matchdays || [1];
    // Only show admin-enabled group matchdays + knockouts whose teams are decided.
    const visible = matches.filter((m) =>
      (m.stage === "group" && m.matchday != null) ? liveMDs.includes(m.matchday) : isFinalised(m)
    );
    const now = new Date();
    if (filter === "upcoming") return visible.filter((m) => new Date(m.kickoff) > now);
    if (filter === "locked") return visible.filter((m) => new Date(m.kickoff) <= now && m.status !== "completed");
    if (filter === "completed") return visible.filter((m) => m.status === "completed");
    return visible;
  }, [matches, filter, active, teams]);

  // Group filtered matches by matchday/stage bucket for dividers.
  // The "Completed" tab is ordered newest-first (last completed match on top).
  const groupedFiltered = useMemo(() => {
    const desc = filter === "completed";
    const map = new Map();
    filtered.forEach((m) => {
      const k = bucketKey(m);
      if (!map.has(k)) map.set(k, { key: k, label: bucketLabel(m, isLeague), matches: [] });
      map.get(k).matches.push(m);
    });
    const now = new Date();
    const arr = [...map.values()];
    arr.forEach((b) => {
      b.matches.sort((x, y) => desc
        ? new Date(y.kickoff) - new Date(x.kickoff)
        : new Date(x.kickoff) - new Date(y.kickoff));
      b.total = b.matches.length;
      b.predicted = b.matches.filter((m) => preds[m.match_id]).length;
      b.lockedCount = b.matches.filter((m) => new Date(m.kickoff) <= now).length;
    });
    arr.sort((a, b) => desc
      ? new Date(b.matches[0].kickoff) - new Date(a.matches[0].kickoff)
      : new Date(a.matches[0].kickoff) - new Date(b.matches[0].kickoff));
    return arr;
  }, [filtered, preds, filter]);

  const buildPayload = (mid, d) => ({
    match_id: mid, home_pred: d.home, away_pred: d.away, is_captain: d.captain, power_play: d.pp,
    penalty_winner: d.penaltyWinner ?? null,
  });

  const persist = async (mid, draftOverride) => {
    const d = draftOverride || draftsRef.current[mid] || blankDraft();
    if (d.home == null || d.away == null) return; // need a full scoreline before saving
    setSaveStatus((s) => ({ ...s, [mid]: "saving" }));
    try {
      const { data } = await api.post(`/tournaments/${active.tournament_id}/predictions`, buildPayload(mid, d));
      setPreds((p) => ({ ...p, [data.match_id]: data }));
      setDrafts((dr) => ({ ...dr, [data.match_id]: blankDraft(data) }));
      setSaveStatus((s) => ({ ...s, [mid]: "saved" }));
    } catch (e) {
      setSaveStatus((s) => ({ ...s, [mid]: "error" }));
      toast.error(e?.response?.data?.detail || "Couldn't save — tap Retry");
    }
  };

  // Clear a saved prediction → "no response" (0 points)
  const clearPrediction = async (mid) => {
    setSaveStatus((s) => ({ ...s, [mid]: "saving" }));
    try {
      await api.delete(`/tournaments/${active.tournament_id}/predictions/${mid}`);
      setPreds((p) => { const n = { ...p }; delete n[mid]; return n; });
      setDrafts((d) => ({ ...d, [mid]: blankDraft(null) }));
      setSaveStatus((s) => { const n = { ...s }; delete n[mid]; return n; });
    } catch (e) {
      setSaveStatus((s) => ({ ...s, [mid]: "error" }));
      toast.error(e?.response?.data?.detail || "Couldn't clear prediction");
    }
  };

  const onChangeDraft = (mid, field, val, saveNow = false) => {
    if ((field === "captain" || field === "pp") && val) {
      const m = matchById[mid];
      const bk = bucketKey(m);
      const clash = matches.find((o) => o.match_id !== mid && bucketKey(o) === bk && draftsRef.current[o.match_id]?.[field]);
      if (clash) {
        if (field === "captain") {
          // Let players MOVE the captain to another match — prompt first to avoid a misclick.
          setConfirmMove({ mid, clashMid: clash.match_id, fromLabel: matchLabel(clash), toLabel: matchLabel(m), mdLabel: bucketLabel(m, isLeague) });
          return;
        }
        toast.error(`Power Play (${ppMult}x) already set for ${bucketLabel(m, isLeague)} (${matchLabel(clash)}). Turn it off there first.`);
        return;
      }
    }
    applyDraft(mid, field, val, saveNow);
  };

  const applyDraft = (mid, field, val, saveNow = false) => {
    touched.current.add(mid);
    const nextDraft = { ...(draftsRef.current[mid] || blankDraft()), [field]: val };
    setDrafts((d) => ({ ...d, [mid]: nextDraft }));
    // Captain / Power Play toggles are explicit actions → autosave immediately
    if (saveNow) persist(mid, nextDraft);
  };

  // Confirmed captain move: unset the old captain in this matchday, set the new one.
  const confirmCaptainMove = () => {
    if (!confirmMove) return;
    const { mid, clashMid } = confirmMove;
    applyDraft(clashMid, "captain", false, true);
    applyDraft(mid, "captain", true, true);
    setConfirmMove(null);
  };

  // Super Jinx — once per half-season; activates 1.5x on ALL of this gameweek's games.
  const toggleSuperJinx = async (matchday) => {
    const gw = `md:${matchday}`;
    const tid = active.tournament_id;
    const isOn = superJinx.used_gameweeks?.includes(gw);
    try {
      if (isOn) {
        await api.delete(`/tournaments/${tid}/super-jinx/${matchday}`);
        toast.success(`Super Jinx removed from GW${matchday}`);
      } else {
        await api.post(`/tournaments/${tid}/super-jinx`, { matchday });
        toast.success(`Super Jinx active for GW${matchday} — all games ${sjMult}×!`);
      }
      const { data } = await api.get(`/tournaments/${tid}/super-jinx/me`);
      setSuperJinx(data);
      api.get(`/tournaments/${tid}/super-jinx/counts`).then((r) => setSjCounts(r.data || {})).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update Super Jinx");
    }
  };

  // Autosave a score change on blur — only when the user touched it AND a FULL
  // scoreline is entered. Clearing both boxes on a saved pick deletes it (no response).
  const onBlurSave = (mid) => {
    if (!touched.current.has(mid)) return;
    const d = draftsRef.current[mid] || blankDraft();
    const p = predsRef.current[mid];
    const bothBlank = d.home == null && d.away == null;
    if (bothBlank) {
      if (p) clearPrediction(mid); // user cleared a previously-saved pick
      return;
    }
    if (d.home == null || d.away == null) return; // incomplete — wait for both boxes
    const dirty = !p || d.home !== p.home_pred || d.away !== p.away_pred || d.captain !== !!p.is_captain || d.pp !== !!p.power_play || (d.penaltyWinner ?? null) !== (p.penalty_winner ?? null);
    if (dirty) persist(mid);
  };

  const onRetry = (mid) => persist(mid);

  if (!active) return null;

  return (
    <div className="px-3 md:px-8 py-6 md:py-8 grid-bg min-h-[calc(100vh-65px)]">
      {confirmMove && createPortal((
        <div className="fixed inset-0 z-[210] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="captain-move-modal">
          <div className="surface-card max-w-md w-full p-6 border border-yellow-400/50">
            <div className="flex items-center gap-2 mb-2">
              <Star size={20} className="text-yellow-400" />
              <h3 className="font-display text-xl uppercase text-yellow-400">Move Captain?</h3>
            </div>
            <p className="text-sm text-zinc-400 mb-5">
              You already have a captain on <span className="text-white">{confirmMove.fromLabel}</span> for{" "}
              <span className="text-white">{confirmMove.mdLabel}</span>. Move it to{" "}
              <span className="text-white">{confirmMove.toLabel}</span> instead?
            </p>
            <div className="flex gap-3">
              <button
                data-testid="captain-move-confirm"
                onClick={confirmCaptainMove}
                className="flex-1 bg-yellow-400 text-black px-5 py-2.5 text-xs uppercase tracking-widest hover:bg-yellow-300 font-display"
              >Move Captain</button>
              <button
                data-testid="captain-move-cancel"
                onClick={() => setConfirmMove(null)}
                className="flex-1 border border-zinc-700 text-zinc-300 px-5 py-2.5 text-xs uppercase tracking-widest hover:border-white/40"
              >Cancel</button>
            </div>
          </div>
        </div>
      ), document.body)}
      {fouls.length > 0 && !foulDismissed && createPortal((
        <div className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="captain-foul-modal">
          <div className="surface-card max-w-lg w-full p-6 border border-primary-accent/50">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle size={20} className="text-primary-accent" />
              <h3 className="font-display text-xl uppercase text-primary-accent">Heads up — rule changed</h3>
            </div>
            <p className="text-sm text-zinc-400 mb-4">
              Captain and Power Play (2x) can now only be used <span className="text-white">once per matchday</span>.
              Some of your current picks break this rule — please remove the extras so only one remains per matchday:
            </p>
            <div className="space-y-2 mb-5 max-h-64 overflow-y-auto">
              {fouls.map((f, i) => (
                <div key={i} className="border border-zinc-800 p-3" data-testid={`foul-${i}`}>
                  <div className="text-xs uppercase tracking-widest text-primary-accent mb-1">{f.chip} · {f.label} ({f.matches.length} used)</div>
                  <div className="text-sm text-zinc-300">{f.matches.map((m) => matchLabel(m)).join(", ")}</div>
                </div>
              ))}
            </div>
            <button
              data-testid="foul-dismiss-btn"
              onClick={() => setFoulDismissed(true)}
              className="bg-primary-accent text-white px-5 py-2.5 text-xs uppercase tracking-widest hover:bg-[var(--primary-hover)]"
            >Got it — I&apos;ll fix it</button>
          </div>
        </div>
      ), document.body)}

      {sjConfirm && createPortal((
        <div className="fixed inset-0 z-[210] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="super-jinx-modal">
          <div className="surface-card max-w-md w-full p-6 border border-purple-400/50">
            <div className="flex items-center gap-2 mb-3">
              <Zap size={20} className="text-purple-300" />
              <h3 className="font-display text-xl uppercase text-purple-300">Play Super Jinx?</h3>
            </div>
            <p className="text-sm text-zinc-400 mb-3">
              This multiplies <span className="text-white">every one of your Gameweek {sjConfirm.matchday} predictions</span> by{" "}
              <span className="text-purple-300 font-display">{sjMult}×</span> — stacking on top of Captain &amp; Power Play.
            </p>
            <div className="border border-purple-400/30 bg-purple-500/10 px-3 py-2 mb-4 text-xs text-zinc-300">
              <span className="text-primary-accent font-display uppercase tracking-widest">Once per half only</span> — you get one Super Jinx for the {sjConfirm.half === 1 ? `first half (GW1–${sjSplit})` : `second half (GW${sjSplit + 1}+)`}. You can move it until the gameweek's first kickoff.
            </div>
            <div className="flex items-center gap-2 text-xs text-purple-300/90 mb-5" data-testid="super-jinx-modal-count">
              <Zap size={12} />
              {sjConfirm.count > 0
                ? `${sjConfirm.count} manager${sjConfirm.count > 1 ? "s have" : " has"} already jinxed GW${sjConfirm.matchday}`
                : `Be the first to jinx GW${sjConfirm.matchday}!`}
            </div>
            <div className="flex gap-2">
              <button
                data-testid="super-jinx-confirm"
                onClick={() => { toggleSuperJinx(sjConfirm.matchday); setSjConfirm(null); }}
                className="flex-1 bg-purple-500 text-white px-5 py-2.5 text-xs uppercase tracking-widest hover:bg-purple-400"
              ><Zap size={12} className="inline mr-1" /> Activate {sjMult}×</button>
              <button
                data-testid="super-jinx-cancel"
                onClick={() => setSjConfirm(null)}
                className="px-5 py-2.5 text-xs uppercase tracking-widest border border-zinc-700 text-zinc-400 hover:border-white/40"
              >Cancel</button>
            </div>
          </div>
        </div>
      ), document.body)}

      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="label-tag mb-1">Main Prediction League</div>
          <h1 className="font-display text-4xl md:text-5xl uppercase">Predict Every Match</h1>
        </div>
        <div className="flex gap-1 surface-card p-1" data-testid="filter-tabs">
          {["upcoming", "locked", "completed", "all"].map((f) => (
            <button
              key={f}
              data-testid={`filter-${f}`}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 text-xs uppercase tracking-widest ${filter === f ? "bg-primary-accent text-white" : "text-zinc-400 hover:text-white"}`}
            >{f}</button>
          ))}
        </div>
      </div>

      <div className="surface-card px-4 py-3 mb-4 flex items-center gap-2 text-xs text-zinc-400" data-testid="autosave-hint">
        <Check size={14} className="text-green-400 flex-shrink-0" />
        <span>Your picks <span className="text-white">save automatically</span> — type both scores (leave blank = no pick, 0 pts). Clear both boxes to remove a pick. Each match locks at its own kickoff.</span>
      </div>

      <div className="surface-card px-4 py-3 mb-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs" data-testid="chips-strip">
        <span className="label-tag">My Chips</span>
        <span className="inline-flex items-center gap-1.5 text-zinc-300">
          <Star size={12} className="text-yellow-400" /> Captain <span className="text-white font-display">{capMult}×</span>
          <span className="text-zinc-600">·</span> Power Play <span className="text-white font-display">{ppMult}×</span>
          <span className="text-zinc-500">— one each per {isLeague ? "gameweek" : "matchday"}</span>
        </span>
        {sjEnabled && (
          <span className="inline-flex items-center gap-2 text-zinc-300" data-testid="chips-super-jinx">
            <Zap size={12} className="text-purple-300" /> Super Jinx <span className="text-white font-display">{sjMult}×</span>
            <span className="inline-flex items-center gap-1">
              <span className={`px-1.5 py-0.5 border text-[10px] uppercase tracking-widest ${superJinx.half1_used ? "border-zinc-700 text-zinc-500" : "border-purple-400/60 text-purple-300 bg-purple-500/10"}`}>
                1st half: {superJinx.half1_used ? `used GW${superJinx.half1_used.split(":")[1]}` : "available"}
              </span>
              <span className={`px-1.5 py-0.5 border text-[10px] uppercase tracking-widest ${superJinx.half2_used ? "border-zinc-700 text-zinc-500" : "border-purple-400/60 text-purple-300 bg-purple-500/10"}`}>
                2nd half: {superJinx.half2_used ? `used GW${superJinx.half2_used.split(":")[1]}` : "available"}
              </span>
            </span>
          </span>
        )}
      </div>

      <div className="text-xs text-zinc-500 mb-4 uppercase tracking-widest flex flex-wrap items-center gap-x-2 gap-y-1">
        <span>Scoring (best tier only, not added): Exact <span className="text-white">{active.config.pts_exact_score}</span> &gt; GD <span className="text-white">{active.config.pts_correct_gd}</span> &gt; Result <span className="text-white">{active.config.pts_correct_result}</span> · Underdog <span className="text-white">{isLeague ? "+1 to +8" : "+2 to +8"}</span></span>
        <span
          className="relative inline-flex"
          onMouseEnter={() => setShowLegend(true)}
          onMouseLeave={() => setShowLegend(false)}
        >
          <button
            data-testid="underdog-legend-toggle"
            onFocus={() => setShowLegend(true)}
            onBlur={() => setShowLegend(false)}
            className="inline-flex items-center gap-1 text-primary-accent hover:text-white transition-colors cursor-help"
          >
            <Info size={12} /> Underdog guide
          </button>
          {showLegend && (
            <div className="absolute z-50 top-full left-0 mt-2 w-[min(92vw,420px)]" data-testid="underdog-legend-popover">
              <UnderdogLegend elo={isLeague} />
            </div>
          )}
        </span>
      </div>

      {filtered.length === 0 && <div className="text-zinc-500">No matches in this view.</div>}
      {groupedFiltered.map((bucket) => (
        <div key={bucket.key} data-testid={`md-section-${bucket.key}`}>
          <div className="flex items-center gap-3 mt-7 mb-3 first:mt-0" data-testid={`md-divider-${bucket.key}`}>
            <div className="h-px flex-1 bg-white/10" />
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="font-display text-sm uppercase tracking-widest text-white">{bucket.label}</span>
              <span
                data-testid={`md-status-${bucket.key}`}
                className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest border ${
                  bucket.lockedCount === bucket.total
                    ? "border-primary-accent/50 text-primary-accent bg-primary-accent/10"
                    : bucket.lockedCount > 0
                    ? "border-yellow-400/50 text-yellow-300 bg-yellow-400/5"
                    : "border-green-500/40 text-green-400 bg-green-500/5"
                }`}
              >
                <Lock size={10} /> {bucket.lockedCount === bucket.total ? "Locked" : bucket.lockedCount > 0 ? "In Play" : "Open"}
              </span>
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">{bucket.predicted}/{bucket.total} picked</span>
              {sjEnabled && bucket.matches[0]?.stage === "group" && bucket.matches[0]?.matchday != null && (() => {
                const bmd = bucket.matches[0].matchday;
                const gw = `md:${bmd}`;
                const isOn = superJinx.used_gameweeks?.includes(gw);
                const half = bmd <= sjSplit ? 1 : 2;
                const usedThisHalf = half === 1 ? superJinx.half1_used : superJinx.half2_used;
                const blockedByHalf = usedThisHalf && usedThisHalf !== gw;
                const locked = bucket.lockedCount > 0;
                const disabled = locked || (!isOn && blockedByHalf);
                const count = sjCounts[gw] || 0;
                return (
                  <span className="inline-flex items-center gap-1.5">
                    <button
                      data-testid={`super-jinx-${bucket.key}`}
                      onClick={() => (isOn ? toggleSuperJinx(bmd) : setSjConfirm({ matchday: bmd, half, count }))}
                      disabled={disabled}
                      title={locked ? "Gameweek locked" : blockedByHalf ? `Super Jinx already used this half (${usedThisHalf?.toUpperCase()})` : `All GW${bmd} games ${sjMult}× points`}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                        isOn ? "border-purple-400 text-purple-300 bg-purple-500/15" : "border-zinc-700 text-zinc-400 hover:border-purple-400"
                      }`}
                    >
                      <Zap size={10} /> {isOn ? `Super Jinx ${sjMult}×` : "Super Jinx"}
                    </button>
                    {count > 0 && (
                      <span className="text-[10px] uppercase tracking-widest text-purple-300/80" data-testid={`super-jinx-count-${bucket.key}`}>
                        {count} manager{count > 1 ? "s" : ""}
                      </span>
                    )}
                  </span>
                );
              })()}
            </div>
            <div className="h-px flex-1 bg-white/10" />
          </div>
          {bucket.matches.map((m) => (
            <MatchRow
              key={m.match_id}
              match={m}
              teams={teams}
              pred={preds[m.match_id]}
              draft={drafts[m.match_id] || blankDraft(preds[m.match_id])}
              onChangeDraft={onChangeDraft}
              onBlurSave={onBlurSave}
              onRetry={onRetry}
              status={saveStatus[m.match_id]}
              finalised={isFinalised(m)}
              usage={bucketUsage[bucketKey(m)]}
              capMult={capMult}
              ppMult={ppMult}
              isLeague={isLeague}
              homeAdv={active?.config?.elo_home_advantage ?? 50}
              koBonus={active?.config?.pts_ko_penalty_bonus ?? 3}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
